import { EMPTY_MODEL_MSG, GGUF_NEEDS_ENDPOINT_MSG, explainLlmHttpError } from "./errors";
import { ggufBlockedWithoutEndpoint, HOUSE_CHAT_HF_NAME } from "./endpoint";
import type { ChatMessage, InferenceTarget, StreamHandlers } from "./types";

export function extractDeltaContent(chunk: unknown): string {
  if (!chunk || typeof chunk !== "object") return "";
  const d = chunk as Record<string, unknown>;
  const choices = d.choices;
  if (!Array.isArray(choices) || !choices.length) return "";
  const c0 = choices[0] as Record<string, unknown>;
  const delta = c0.delta as Record<string, unknown> | undefined;
  if (delta) {
    if (typeof delta.content === "string") return delta.content;
    if (Array.isArray(delta.content)) {
      return delta.content
        .map((p) =>
          typeof p === "string"
            ? p
            : p && typeof p === "object" && typeof (p as { text?: string }).text === "string"
              ? (p as { text: string }).text
              : "",
        )
        .join("");
    }
  }
  const msg = c0.message as Record<string, unknown> | undefined;
  if (typeof msg?.content === "string") return msg.content;
  if (typeof c0.text === "string") return c0.text;
  return "";
}

function extractNonStream(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  return extractDeltaContent(data);
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function resolveChatUrl(baseUrl: string): string {
  const base = baseUrl.replace(/\/$/, "");
  return `${base}/chat/completions`;
}

/** House ChatForge never posts `local` — llama.cpp on the HF box 404s that id. */
export function chatRequestModel(model?: string | null): string {
  const s = (model || "").trim();
  if (!s || /^(local|default|none)$/i.test(s)) return HOUSE_CHAT_HF_NAME;
  return s;
}

export async function streamChat(
  target: InferenceTarget,
  messages: Pick<ChatMessage, "role" | "content">[],
  handlers: StreamHandlers = {},
): Promise<string> {
  if (ggufBlockedWithoutEndpoint(target.model, target.baseUrl, target.source)) {
    const err = new Error(GGUF_NEEDS_ENDPOINT_MSG);
    handlers.onError?.(err);
    throw err;
  }

  const url = resolveChatUrl(target.baseUrl);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Forge-App": "chatforge",
    "X-Client-Product": "chatforge",
    "X-Suite-Tool": "chat",
    "Idempotency-Key": newIdempotencyKey(),
  };
  headers["X-Forge-Endpoint"] =
    target.sourceId?.trim() || chatRequestModel(target.model);
  if (target.apiKey.trim()) {
    headers.Authorization = `Bearer ${target.apiKey.trim()}`;
  }

  const body: Record<string, unknown> = {
    model: chatRequestModel(target.model),
    messages: messages.map((m) => ({ role: m.role, content: m.content })),
    temperature: 0.7,
    max_tokens: 4096,
    stream: true,
  };
  if (target.source !== "endpoint") {
    body.client = "chatforge";
    body.tool = "chat";
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: handlers.signal,
    });
  } catch (e) {
    const err = e instanceof Error ? e : new Error(String(e));
    if (err.name === "AbortError") throw err;
    const hint = /bookforge-relay|forgesuiteui/i.test(url)
      ? " Browser could not reach BookForge relay (CORS/network). Hard-refresh, stay signed in, then retry."
      : " Check that the engine is up.";
    const wrapped = new Error(`LLM request failed: ${err.message}.${hint}`);
    handlers.onError?.(wrapped);
    throw wrapped;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const msg = explainLlmHttpError(res.status, text, url);
    const err = new Error(msg);
    handlers.onError?.(err);
    throw err;
  }

  if (!res.body) {
    const data = await res.json();
    const full = extractNonStream(data);
    if (!full.trim()) {
      const err = new Error(EMPTY_MODEL_MSG);
      handlers.onError?.(err);
      throw err;
    }
    handlers.onToken?.(full);
    handlers.onDone?.(full);
    return full;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let full = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith("data:")) continue;
        const payload = trimmed.slice(5).trim();
        if (payload === "[DONE]") continue;
        try {
          const json = JSON.parse(payload) as { error?: unknown };
          if (json && typeof json === "object" && json.error) {
            const msg = explainLlmHttpError(res.status >= 400 ? res.status : 502, payload, url);
            const err = new Error(msg);
            handlers.onError?.(err);
            throw err;
          }
          const token = extractDeltaContent(json);
          if (token) {
            full += token;
            handlers.onToken?.(token);
          }
        } catch (e) {
          if (e instanceof SyntaxError) continue;
          throw e;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!full.trim()) {
    const err = new Error(EMPTY_MODEL_MSG);
    handlers.onError?.(err);
    throw err;
  }
  handlers.onDone?.(full);
  return full;
}
